import { useSyncExternalStore } from 'react';

/**
 * Motyw light/dark/system z zapisem w localStorage i klasa `dark` na <html>.
 *
 * JEDEN wspolny stan dla calej aplikacji (useSyncExternalStore). Wczesniej
 * kazde wywolanie hooka mialo wlasny useState — po kliknieciu przelacznika
 * inne komponenty nie widzialy zmiany (audyt 2026-09-29: przelacznik trzeba
 * bylo klikac 2x). Skrypt w index.html ustawia klase przed renderem (bez
 * mrugniecia) wg tej samej logiki.
 */
export type ThemePref = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'coreltb.theme';
const listeners = new Set<() => void>();

function readPref(): ThemePref {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // localStorage niedostepny (tryb prywatny) — zostaje 'system'
  }
  return 'system';
}

function systemPrefersDark(): boolean {
  return Boolean(window.matchMedia?.('(prefers-color-scheme: dark)').matches);
}

function resolve(pref: ThemePref): ResolvedTheme {
  if (pref === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return pref;
}

let currentPref: ThemePref = typeof window === 'undefined' ? 'system' : readPref();

function applyToDom() {
  document.documentElement.classList.toggle('dark', resolve(currentPref) === 'dark');
}

function emit() {
  applyToDom();
  for (const l of listeners) l();
}

if (typeof window !== 'undefined') {
  applyToDom();
  window.matchMedia?.('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (currentPref === 'system') emit();
  });
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const getSnapshot = () => `${currentPref}:${resolve(currentPref)}`;

export function setThemePref(next: ThemePref) {
  currentPref = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // brak zapisu — motyw dziala do odswiezenia
  }
  emit();
}

export function useTheme() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, () => 'system:light');
  const [pref, resolved] = snapshot.split(':') as [ThemePref, ResolvedTheme];
  return { pref, resolved, setPref: setThemePref };
}
